/**
 * Class to manage and cancel active API requests by module name using AbortController.
 * Ensures only one active request per module and allows global or module-specific cancellation.
 */
class ApiCancellationManager {
  constructor() {
    /**
     * Stores active AbortControllers keyed by module name.
     * @type {Map<string, AbortController>}
     */
    this.activeControllers = new Map();
  }

  /**
   * Adds a new AbortController for a module. Cancels any existing request for the module.
   *
   * @param {string} moduleName - Unique identifier for the request/module.
   * @param {AbortController} controller - The AbortController instance to manage.
   */
  addController(moduleName, controller) {
    // Cancel existing request if one exists
    if (this.activeControllers.has(moduleName)) {
      const existing = this.activeControllers.get(moduleName);
      if (!existing.signal.aborted) {
        existing.abort(`Replaced by new request in ${moduleName}`);
      }
    }

    this.activeControllers.set(moduleName, controller);
  }

  /**
   * Removes the AbortController associated with a given module.
   *
   * @param {string} moduleName - The module whose controller should be removed.
   */
  removeController(moduleName) {
    this.activeControllers.delete(moduleName);
  }

  /**
   * Cancels the active request associated with the given module name.
   *
   * @param {string} moduleName - The module to cancel the request for.
   */
  cancelModuleRequests(moduleName) {
    const controller = this.activeControllers.get(moduleName);
    if (controller && !controller.signal.aborted) {
      controller.abort(`Cancelled by leaving ${moduleName}`);
    }
    this.removeController(moduleName);
  }

  /**
   * Cancels all active requests.
   *
   * @param {string} [reason="Navigation cancelled requests"] - Reason for cancellation.
   */
  cancelAllRequests(reason = "Navigation cancelled requests") {
    this.activeControllers.forEach((controller) => {
      if (!controller.signal.aborted) {
        controller.abort(reason);
      }
    });
    this.activeControllers.clear();
  }
}

/**
 * Singleton instance of ApiCancellationManager used across the app.
 */
export const apiCancellationManager = new ApiCancellationManager();
